import { formatCurrencyAmount } from '@/lib/formatMoney';

/** Mirrors GroupDirectoryController::summarise() in kashround-api. */
export interface GroupSummary {
  uuid: string;
  code: string;
  name: string;
  countryName: string | null;
  memberCount: number;
  /** null means the tier has no seat cap (e.g. Enterprise), not "unknown". */
  memberLimit: number | null;
  tierName: string | null;
  subscriptionStatus: string | null;
  createdAt: string;
}

export interface GroupListMeta {
  total: number;
  currentPage: number;
  lastPage: number;
  perPage: number;
}

export interface AppliedPromoCode {
  code: string;
  discountType: 'percentage' | 'free_period' | 'fixed';
  discountValue: number;
  /** null for a code the group chose at checkout and will redeem when it pays. */
  redeemedAt: string | null;
}

export interface AmendableInvoice {
  invoiceNumber: string;
  amount: number;
  currency: string;
}

export interface GroupSubscription {
  tierName: string | null;
  status: string;
  billingCycle: string | null;
  /**
   * Tiers are listed in USD and converted at invoicing time, so these are list
   * prices, not what the group was last charged. A per_user tier carries a
   * flat `price` of 0 and bills on `pricePerUser`, so reading `price` alone
   * would show Enterprise as free.
   */
  pricingModel: string | null;
  price: number | null;
  pricePerUser: number | null;
  billedUserCount: number | null;
  currency: string | null;
  memberLimit: number | null;
  startsAt: string | null;
  endsAt: string | null;
  trialEndsAt: string | null;
  /** The one promo code this subscription holds, or null. */
  appliedPromoCode: AppliedPromoCode | null;
  /** Issued invoices a percentage code applied now would reduce. */
  amendableInvoices: AmendableInvoice[];
}

/**
 * email and phone are null for every member who is not an officer. The API
 * withholds them rather than the UI hiding them, so they are never in the
 * payload to begin with.
 */
export interface GroupMember {
  name: string;
  role: string;
  isAdmin: boolean;
  joinedAt: string;
  email: string | null;
  phone: string | null;
}

/** Mirrors GroupDirectoryController::show(). */
export interface GroupDetail {
  uuid: string;
  code: string;
  name: string;
  description: string | null;
  countryName: string | null;
  address: string | null;
  district: string | null;
  foundedAt: string | null;
  isRegistered: boolean;
  registrationNumber: string | null;
  createdAt: string;
  memberCount: number;
  subscription: GroupSubscription | null;
  members: GroupMember[];
}

/** The sorts the table can actually produce. The API also accepts 'name'. */
export type GroupSort = 'newest' | 'members_asc' | 'members_desc';

/** "3 / 10" when the tier caps seats, plain "3" when it does not. */
export function formatSeats(memberCount: number, memberLimit: number | null): string {
  return memberLimit === null ? String(memberCount) : `${memberCount} / ${memberLimit}`;
}

const CYCLE_NOUN: Record<string, string> = { annual: 'year', monthly: 'month' };

/**
 * The plan's list price in words. A per_user tier prices per member, so
 * rendering its flat `price` would report the platform's largest accounts
 * as free.
 */
export function formatPlanPrice(subscription: GroupSubscription): string {
  const { currency, billingCycle, pricingModel, price, pricePerUser, billedUserCount } = subscription;
  if (!currency) {
    return 'Not set';
  }

  const per = billingCycle ? ` per ${CYCLE_NOUN[billingCycle] ?? billingCycle}` : '';

  if (pricingModel === 'per_user') {
    if (pricePerUser === null) {
      return 'Not set';
    }
    const billed = billedUserCount === null ? '' : ` (billed for ${billedUserCount})`;
    return `${formatCurrencyAmount(pricePerUser, currency)} per user${per}${billed}`;
  }

  return price === null ? 'Not set' : `${formatCurrencyAmount(price, currency)}${per}`;
}
